import { StarIcon } from '@heroicons/react/20/solid';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  HireSource,
  HireSourceWorker,
  registerHireSourcePrompter,
  SOURCED_FROM_OPTIONS,
  SourcedFrom,
} from './hireSourcing';

import SurgeDialog from '@/routes/Agency/PaySurge/components/SurgeDialog';

import './hireSourcing.css';

type Pending = {
  worker: HireSourceWorker;
  resolve: (source: HireSource | null) => void;
};

const NOTE_MAX = 255;

/**
 * The "Confirm how you found this worker" dialog (GRAVY-10443; Figma
 * 38491:96940, 40569:193560), mounted once in App and opened by
 * `withHireSourcing` whenever graphy asks.
 *
 * Submit stays disabled until a source is chosen, and for Other until the free
 * text is filled in — the same rule graphy enforces. It shares the GravyWork
 * 2.0 dialog shell the surge pay dialogs use.
 */
const HireSourcingPrompt = () => {
  const [pending, setPending] = useState<Pending | null>(null);
  const [sourcedFrom, setSourcedFrom] = useState<SourcedFrom | ''>('');
  const [note, setNote] = useState('');
  const pendingRef = useRef<Pending | null>(null);
  pendingRef.current = pending;

  useEffect(
    () =>
      registerHireSourcePrompter(
        (worker) =>
          new Promise<HireSource | null>((resolve) => {
            // A second hire asking while one is open answers the first as
            // cancelled rather than leaving it waiting forever.
            pendingRef.current?.resolve(null);
            setSourcedFrom('');
            setNote('');
            setPending({ worker, resolve });
          }),
      ),
    [],
  );

  const finish = useCallback((source: HireSource | null) => {
    pendingRef.current?.resolve(source);
    setPending(null);
  }, []);

  if (!pending) return null;

  const { worker } = pending;
  const isOther = sourcedFrom === 'other';
  const canSubmit = sourcedFrom !== '' && (!isOther || note.trim() !== '');

  return (
    <SurgeDialog
      size="sm"
      testId="hireSourcing"
      title="Confirm how you found this worker"
      subtitle="This helps us understand how shifts get filled."
      onClose={() => finish(null)}
    >
      <div className="hiresource-worker" data-testid="hireSourcingWorker">
        {worker.avatar_url ? (
          <img className="hiresource-avatar" src={worker.avatar_url} alt="" />
        ) : (
          <span className="hiresource-avatar is-initials" aria-hidden="true">
            {(worker.name ?? '?').slice(0, 1)}
          </span>
        )}
        <div className="hiresource-meta">
          <span className="hiresource-name">{worker.name}</span>
          <span className="hiresource-stats">
            {worker.average_rating ? (
              <span className="hiresource-stat">
                <StarIcon className="hiresource-star" aria-hidden="true" />
                <b>{worker.average_rating.toFixed(1)}</b>
                <span>({worker.ratings_count ?? 0})</span>
              </span>
            ) : null}
            {worker.skill_name && (
              <span className="hiresource-stat">
                <b>{worker.skill_shifts ?? 0}</b>
                <span>{worker.skill_name} shifts</span>
              </span>
            )}
            <span className="hiresource-stat">
              <b>{worker.shifts_with_client ?? 0}</b>
              <span>Shifts with you</span>
            </span>
          </span>
        </div>
      </div>

      <div className="hiresource-fields">
        <label className="paysurge-field">
          <span className="paysurge-label">Sourced from</span>
          <span className="paysurge-control is-select">
            <select
              aria-label="Sourced from"
              data-testid="hireSourcingSelect"
              value={sourcedFrom}
              onChange={(e) => setSourcedFrom(e.target.value as SourcedFrom)}
            >
              <option value="" disabled>
                Select an option
              </option>
              {SOURCED_FROM_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <ChevronDownIcon aria-hidden="true" />
          </span>
        </label>
        {isOther && (
          <input
            className="paysurge-input"
            aria-label="How you found this worker"
            data-testid="hireSourcingNote"
            maxLength={NOTE_MAX}
            placeholder="Tell us how you found them"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        )}
      </div>

      <div className="paysurge-actions">
        <button
          type="button"
          className="paysurge-btn is-outline"
          onClick={() => finish(null)}
        >
          Cancel
        </button>
        <button
          type="button"
          className="paysurge-btn is-primary is-wide"
          data-testid="hireSourcingSubmit"
          disabled={!canSubmit}
          onClick={() =>
            sourcedFrom &&
            finish({
              sourcedFrom,
              note: isOther ? note.trim() : undefined,
            })
          }
        >
          Submit
        </button>
      </div>
    </SurgeDialog>
  );
};

export default HireSourcingPrompt;
